import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Section, { FadeIn } from "@/components/Section";
import { TARGET_GROUPS, BRAND } from "@/lib/brand";

const PHOTOS = {
  azubis: BRAND.photos.coaching,
  fachkraefte: BRAND.photos.coaching,
  fuehrungskraefte: BRAND.photos.portrait,
};

export default function TargetGroupsPage() {
  return (
    <div data-testid="target-groups-page" className="pt-36 lg:pt-44">
      <Section padded={false} className="pb-20">
        <div className="max-w-4xl">
          <FadeIn>
            <div className="overline mb-6">Zielgruppen</div>
            <h1 className="font-display text-[44px] sm:text-[60px] lg:text-[80px] leading-[0.98] tracking-[-0.035em] text-white">
              Drei Phasen.
              <br />
              <span className="font-editorial italic text-white/90">
                Eine klare Ansprache.
              </span>
            </h1>
            <p className="mt-8 text-white/65 text-lg leading-relaxed">
              Azubis, Fachkräfte und Führungskräfte. Jede Gruppe bringt andere
              Fragen mit. Ich kenne die Sprache jeder Karrierestufe — aus
              eigener Praxis.
            </p>
          </FadeIn>
        </div>
      </Section>

      {TARGET_GROUPS.map((g, i) => {
        const isAlt = i % 2 === 1;
        return (
          <section
            id={g.slug}
            key={g.slug}
            data-testid={`target-group-section-${g.slug}`}
            className={`py-24 lg:py-32 ${isAlt ? "bg-[#101B34]" : ""}`}
          >
            <div className="container-edge">
              <div
                className={`grid grid-cols-1 lg:grid-cols-12 gap-14 items-center ${
                  isAlt ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-7">
                  <FadeIn>
                    <div className="flex items-center gap-5 mb-6">
                      <span className="kpi-number text-[96px] lg:text-[140px] text-white/10 leading-none">
                        {g.code}
                      </span>
                      <div>
                        <div className="overline">{g.kicker}</div>
                        <div className="mt-2 font-display text-2xl lg:text-3xl text-white/80 tracking-tight">
                          {g.label}
                        </div>
                      </div>
                    </div>
                    <h2 className="font-display text-4xl lg:text-[56px] tracking-[-0.02em] text-white leading-[1.02]">
                      {g.headline}
                    </h2>
                    <p className="mt-6 text-white/65 text-lg leading-relaxed max-w-xl">
                      {g.description}
                    </p>

                    <div className="mt-10">
                      <div className="overline mb-4">Mögliche Themen</div>
                      <ul className="grid sm:grid-cols-2 gap-3">
                        {g.topics.map((t) => (
                          <li
                            key={t}
                            className="flex items-start gap-2 text-white/75 text-[15px]"
                          >
                            <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-brand-400 flex-shrink-0" />
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to="/kontakt"
                      data-testid={`target-cta-${g.slug}`}
                      className="btn-primary mt-10"
                    >
                      Anfragen <ArrowUpRight size={18} />
                    </Link>
                  </FadeIn>
                </div>

                <div className="lg:col-span-5">
                  <FadeIn delay={0.1}>
                    <div className="relative aspect-[4/5] rounded-[12px] overflow-hidden border border-white/10">
                      <img
                        src={PHOTOS[g.slug]}
                        alt={`Coaching für ${g.label}`}
                        className="w-full h-full object-cover img-editorial"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1324]/70 via-transparent to-transparent" />
                    </div>
                  </FadeIn>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <Section>
        <div className="surface p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="overline mb-4">Ihr Kontakt</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-white leading-[1.1]">
              Nicht sicher, welche Ansprache zu Ihrer Situation passt?
            </h2>
          </div>
          <Link to="/kontakt" data-testid="target-final-cta" className="btn-primary">
            Termin anfragen <ArrowUpRight size={18} />
          </Link>
        </div>
      </Section>
    </div>
  );
}
